import type { DialogShortData } from '@shared/api';
import { Flex } from 'antd';
import type { FC } from 'react';
import styles from './dialog-item.module.scss';
import {
  Avito,
  CreatorMax,
  CreatorTelegram,
  CreatorVk,
  CreatorWidgetSmall,
  LeadCreated,
} from '@shared/assets/icons/components';
import { Text } from '@shared/ui/antd';
import { truncateText } from '@shared/utils/truncate-text';
import { DateService } from '@shared/services/date';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

interface DialogItemProps {
  dialog: DialogShortData;
  selectedDialogId: number | null;
  onSelect: (dialog: DialogShortData) => void;
}

export const DialogItem: FC<DialogItemProps> = ({ dialog, selectedDialogId, onSelect }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'dialogs' });
  const isSelected = dialog.client_id === selectedDialogId;

  const isTelegramBot = dialog.channel_type === 'telegram_bot';
  const isTelegram = dialog.channel_type === 'telegram';
  const isWidget = dialog.channel_type === 'widget';
  const isVk = dialog.channel_type === 'vk_bot';
  const isMax = dialog.channel_type === 'max_bot';
  const isAvito = dialog.channel_type === 'avito';

  const formattedTime = (() => {
    if (!dialog.last_message_at) return '-';

    const date = dayjs(dialog.last_message_at);
    const now = dayjs();

    if (date.isSame(now, 'day')) {
      return DateService.format(dialog.last_message_at, 'HH:mm');
    } else {
      return DateService.format(dialog.last_message_at, 'DD.MM.YYYY');
    }
  })();

  const hasLeadCreated = Boolean(dialog.lead_created_at);
  const anonymousLabel = t('list.anonymous', { id: dialog.client_id });

  return (
    <Flex
      className={classNames(styles.root, { [styles.selected]: isSelected })}
      align="center"
      gap={12}
      onClick={() => onSelect(dialog)}
    >
      <Flex className={styles.avatar__wrapper}>
        <div
          className={styles.avatar}
          style={{
            backgroundImage: dialog.client_avatar_url
              ? `url(${dialog.client_avatar_url})`
              : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        {isWidget && (
          <span
            className={classNames(styles.onlineBadge, {
              [styles.onlineBadgeOffline]: !dialog.is_online,
            })}
          />
        )}
      </Flex>

      <Flex vertical gap={4} className={styles.text__wrapper}>
        <Flex align="center" justify="space-between" className={styles.header}>
          <Flex align="center" gap={8} className={styles.headerLeft}>
            {(isTelegram || isTelegramBot) && <CreatorTelegram />}
            {isWidget && <CreatorWidgetSmall />}
            {isVk && <CreatorVk />}
            {isMax && <CreatorMax width={16} height={16} />}
            {isAvito && <Avito width={16} height={16} />}

            <Flex align="center" gap={4} className={styles.time}>
              <Text
                variant="md"
                maxLength={10}
                hasTooltip
                fontWeight={500}
                className={styles.clientName}
              >
                {dialog.client_name || anonymousLabel}
              </Text>

              {hasLeadCreated ? <LeadCreated className={styles.leadIcon} /> : null}
            </Flex>
          </Flex>

          <Text variant="sm" fontWeight={400} className={styles.text__time}>
            {formattedTime}
          </Text>
        </Flex>

        <Flex align="center" gap={8} className={styles.previewRow}>
          <Text variant="sm" fontWeight={400} className={styles.text}>
            {truncateText(dialog.last_message, 25).truncatedText || '-'}
          </Text>

          {dialog.is_test ? <span className={styles.testTag}>{t('list.test')}</span> : null}
        </Flex>
      </Flex>
    </Flex>
  );
};
